import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";
import { Icon, IconType } from "../../../common/icons/Icon";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";
import { AiMarkdown } from "../markdown/AiMarkdown";
import { aiAssistantTexts } from "../models/aiAssistantModels";
import { AiChatEntry } from "../utilities/aiChatEntriesUtility";
import { AiSupportDraftCard } from "./AiSupportDraftCard";
import { AiSupportTicketCard } from "./AiSupportTicketCard";
import styles from "./AiMessages.module.scss";

interface IAiChatEntryViewProps {
  entry: AiChatEntry;
  onRetry: () => void;
  onOpenImage: (url: string) => void;
}

/** One row of the chat: a question, an answer, a status line, an error, a notice, a draft or a created ticket. */
export const AiChatEntryView = ({ entry, onRetry, onOpenImage }: IAiChatEntryViewProps) => {
  // The turn being sent comes in with the entry animation; stored rows don't replay it.
  const row = (className: string) => (entry.live ? `${className} ${styles.entry_live}` : className);

  switch (entry.kind) {
    case "user":
      return (
        <div className={row(styles.row_user)}>
          <div className={styles.bubble_user}>
            <RenderWhen condition={entry.images.length > 0}>
              <div className={styles.bubble_images}>
                {entry.images.map((image) => (
                  <button
                    key={image.id || image.url}
                    type="button"
                    className={styles.bubble_image_btn}
                    onClick={() => onOpenImage(image.url)}
                    aria-label={aiAssistantTexts.openImage}
                  >
                    <img className={styles.bubble_image} src={image.url} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            </RenderWhen>
            <RenderWhen condition={!!entry.text}>
              <div className={styles.bubble_user_text}>{entry.text}</div>
            </RenderWhen>
          </div>
        </div>
      );

    case "assistant":
      return (
        <div className={row(styles.row_assistant)}>
          <span className={styles.avatar} aria-hidden="true">
            <Icon type={IconType.Sparkle} width="15px" height="15px" color="currentColor" />
          </span>
          <div className={styles.bubble_assistant}>
            <AiMarkdown text={entry.text} />
          </div>
        </div>
      );

    case "status":
      return (
        <div className={row(styles.row_assistant)}>
          <span className={styles.avatar} aria-hidden="true">
            <Icon type={IconType.Sparkle} width="15px" height="15px" color="currentColor" />
          </span>
          {entry.toolLabel ? (
            <div className={styles.status_line}>{entry.toolLabel}</div>
          ) : (
            <div className={styles.typing} aria-label="Асистентът пише">
              <span className={styles.typing_dot} />
              <span className={styles.typing_dot} />
              <span className={styles.typing_dot} />
            </div>
          )}
        </div>
      );

    case "error":
      return (
        <div className={row(styles.row_assistant)}>
          <span className={styles.avatar} aria-hidden="true">
            <Icon type={IconType.Sparkle} width="15px" height="15px" color="currentColor" />
          </span>
          <div className={styles.error_row} role="alert">
            <span className={styles.error_text}>{entry.text}</span>
            <RenderWhen condition={entry.canRetry}>
              <DefaultButton
                className={styles.error_retry}
                design="link"
                width="auto"
                label={aiAssistantTexts.retry}
                onClick={onRetry}
              />
            </RenderWhen>
          </div>
        </div>
      );

    case "notice":
      return <div className={row(styles.notice)}>{entry.text}</div>;

    case "draft":
      return (
        <div className={row(styles.row_draft)}>
          <AiSupportDraftCard messageId={entry.messageId} draft={entry.draft} supportChatId={entry.supportChatId} />
        </div>
      );

    case "ticket":
      return (
        <div className={row(styles.row_ticket)}>
          <AiSupportTicketCard ticket={entry.ticket} />
        </div>
      );

    default:
      return null;
  }
};
